import { useState } from "react";
import { postSimulatePayment } from "../../api/endpoints";
import { describeCommerceError } from "../../commerce/errors";
import type { CardConfirmOutcome } from "../../commerce/stripeResult";

export const SIMULATED_NOTICE =
  "Simulated payments (local development only): there is no card form, no Stripe and no money moves. The server plays the card confirmation and the purchase still completes only when the server fulfils the order.";

/**
 * Stand-in for the card form when the API runs `PAYMENTS_PROVIDER=simulated` (local development
 * only; refused on staging and production). It asks the API's dev route to play a successful or a
 * declined card confirmation; the checkout then polls the order exactly as after a real card.
 */
export function SimulatedPaymentStep({
  orderId,
  amountLabel,
  busy,
  onConfirmStart,
  onConfirmResult,
}: {
  orderId: string;
  amountLabel: string;
  busy: boolean;
  onConfirmStart(): boolean;
  onConfirmResult(outcome: CardConfirmOutcome): void;
}) {
  const [sending, setSending] = useState<"succeed" | "decline" | null>(null);
  const run = async (outcome: "succeed" | "decline") => {
    if (!onConfirmStart()) return;
    setSending(outcome);
    try {
      const view = await postSimulatePayment(orderId, outcome);
      onConfirmResult(
        outcome === "decline" && view.status === "awaiting_payment"
          ? { kind: "retry_card", message: "The simulated card was declined. Nothing was charged. Try again." }
          : { kind: "submitted" },
      );
    } catch (err) {
      onConfirmResult({ kind: "check_order", message: describeCommerceError(err, { stage: "payment_intent" }).message });
    } finally {
      setSending(null);
    }
  };
  return (
    <div className="stack" data-testid="simulated-payment" data-movement-block="true">
      <div className="notice notice-warning small">{SIMULATED_NOTICE}</div>
      <div className="row">
        <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void run("succeed")} data-testid="simulate-succeed">
          {sending === "succeed" ? "Confirming…" : `Simulate a successful payment (${amountLabel})`}
        </button>
        <button type="button" className="btn" disabled={busy} onClick={() => void run("decline")} data-testid="simulate-decline">
          {sending === "decline" ? "Confirming…" : "Simulate a declined card"}
        </button>
      </div>
    </div>
  );
}
